import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import api from '../../services/api';
import { richTextSummary } from '../../components/public/RichContent';
import { formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import { phoneHref } from '../../utils/phone';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './HomePage.css';

const fallbackRepresentatives = [
  { id: 1, name: 'श्री. देवेंद्र फडणवीस', designation: 'मा. मुख्यमंत्री', photo: 'https://randomuser.me/api/portraits/men/32.jpg' },
  { id: 2, name: 'श्री. एकनाथ शिंदे', designation: 'मा. उपमुख्यमंत्री', photo: 'https://randomuser.me/api/portraits/men/45.jpg' },
  { id: 3, name: 'श्रीमती सुप्रिया सुळे', designation: 'मा. खासदार', photo: 'https://randomuser.me/api/portraits/women/44.jpg' },
  { id: 4, name: 'श्री. अजय मोरे', designation: 'मा. सदस्य', photo: 'https://randomuser.me/api/portraits/men/67.jpg' },
  { id: 5, name: 'श्री. अनिकेत कदम', designation: 'मा. सदस्य', photo: 'https://randomuser.me/api/portraits/men/82.jpg' },
];

const serviceNames = {
  birth: 'जन्म दाखला',
  death: 'मृत्यू दाखला',
  residence: 'रहिवासी दाखला',
  caste: 'जात प्रमाणपत्र',
  bpl: 'दारिद्र्य रेषेखालील दाखला',
  character: 'चारित्र्य प्रमाणपत्र',
  disability: 'दिव्यांग प्रमाणपत्र',
  income: 'उत्पन्न प्रमाणपत्र',
  marriage: 'विवाह नोंदणी प्रमाणपत्र',
  migration: 'स्थलांतर प्रमाणपत्र',
  senior_citizen: 'ज्येष्ठ नागरिक प्रमाणपत्र',
};

const serviceIcons = {
  birth: '👥',
  death: '🏅',
  residence: '⌂',
  caste: '▤',
  bpl: '▣',
  character: '✓',
  disability: '♿',
  income: '₹',
  marriage: '◇',
  migration: '→',
  senior_citizen: '♙',
};

const serviceTones = {
  birth: 'blue',
  death: 'orange',
  residence: 'purple',
  income: 'orange',
  marriage: 'pink',
};

const shortName = name => (name || 'देऊळगाव गाडा').replace(/^ग्रामपंचायत\s*/i, '').trim();
const formatDate = value => value ? new Date(value).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const truncateText = (text, maxLength = 20) => text && text.length > maxLength ? `${text.substring(0, maxLength).trim()}...` : text;
const formatCategory = (cat) => cat ? String(cat).charAt(0).toUpperCase() + String(cat).slice(1).toLowerCase() : 'नवीन नोटीस';

export default function HomePage() {
  const { tenantSlug } = useParams();
  const { tenantData, loading } = useTenant();
  const [directoryCounts, setDirectoryCounts] = useState({ schemes: 0, facilities: 0, staff: 0 });
  const [activeBanner, setActiveBanner] = useState(0);

  useEffect(() => {
    if (!tenantSlug) return;
    Promise.allSettled([
      api.get(`/tenant/${tenantSlug}/schemes`),
      api.get(`/tenant/${tenantSlug}/facilities`),
      api.get(`/tenant/${tenantSlug}/staff`),
    ]).then(([schemes, facilities, staff]) => {
      setDirectoryCounts({
        schemes: schemes.status === 'fulfilled' ? (schemes.value.data?.schemes || schemes.value.data || []).length : 0,
        facilities: facilities.status === 'fulfilled' ? (facilities.value.data || []).length : 0,
        staff: staff.status === 'fulfilled' ? (staff.value.data || []).length : 0,
      });
    });
  }, [tenantSlug]);

  const tenant = tenantData?.tenant;
  const pages = tenantData?.pages || [];
  let banners = tenant?.banner_url ? [{ url: tenant.banner_url, title: tenant.name }] : [];
  try {
    const parsed = JSON.parse(pages.find(page => page.slug === 'banners')?.content || '[]');
    if (Array.isArray(parsed) && parsed.length) banners = parsed;
  } catch {}

  useEffect(() => setActiveBanner(0), [banners.length]);
  useEffect(() => {
    if (banners.length < 2) return undefined;
    const interval = window.setInterval(() => setActiveBanner(index => (index + 1) % banners.length), 5500);
    return () => window.clearInterval(interval);
  }, [banners.length]);

  if (loading || !tenantData) return <LoadingSpinner fullPage message="ग्रामपंचायत माहिती लोड होत आहे..." />;

  const { notices = [], gallery = [], fees = [] } = tenantData;
  const galleryImages = gallery.filter(item => item.media_type !== 'video');
  const banner = banners[activeBanner]?.url || tenant?.banner_url;
  let representatives = fallbackRepresentatives;
  try {
    const raw = pages.find(page => page.slug === 'representatives')?.content || [];
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed) && parsed.length) representatives = parsed;
  } catch {}

  const seenServiceTypes = new Set();
  const services = fees
    .filter(fee =>
      fee?.is_active !== false
      && (fee?.service_provider || 'grampanchayat') === 'grampanchayat'
      && fee?.service_type)
    .filter(fee => {
      if (seenServiceTypes.has(fee.service_type)) return false;
      seenServiceTypes.add(fee.service_type);
      return true;
    })
    .slice(0, 8)
    .map(fee => ({
        icon: serviceIcons[fee.service_type] || '▤',
        label: fee.service_name || serviceNames[fee.service_type] || fee.service_type.replace(/_/g, ' '),
        tone: serviceTones[fee.service_type] || 'blue',
        type: fee.service_type,
      }));

  const aboutSections = Array.isArray(tenant?.about_sections)
    ? [...tenant.about_sections].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  const villageIntro = richTextSummary(tenant?.about_text || aboutSections[0]?.content || pages.find(page => page.slug === 'about')?.content, 340);
  const discoveryItems = [
    { icon: '▤', label: 'नागरी सेवा', count: fees.length, detail: `ऑनलाईन उपलब्ध ${formatMarathiNumber(fees.length || 0)} सेवा`, path: '/services', tone: 'blue' },
    { icon: '◉', label: 'योजना', count: directoryCounts.schemes, detail: 'गाव विकास व कल्याण योजना', path: '/schemes', tone: 'green' },
    { icon: '⌂', label: 'विभाग व सुविधा', count: directoryCounts.facilities, detail: 'उपलब्ध सुविधा व विभाग', path: '/facilities', tone: 'orange' },
    { icon: '👥', label: 'अधिकारी व कर्मचारी', count: directoryCounts.staff, detail: 'ग्रामपंचायत कर्मचारी विवरण', path: '/staff', tone: 'purple' },
    { icon: '▧', label: 'फोटो गॅलरी', count: gallery.length, detail: 'गाव, कार्यक्रम व विकासकामे', path: '/gallery', tone: 'pink' },
    { icon: '▣', label: 'नोटीस व सूचना', count: notices.length, detail: 'सर्व नोटीस व सूचना', path: '/notices', tone: 'teal' },
  ];
  const metrics = [
    { icon: '♜', label: 'गावाची स्थापना', value: tenant?.village_established_year || '—' },
    { icon: '▥', label: 'ग्रामपंचायत स्थापना', value: tenant?.established_year || '—' },
    { icon: '●', label: 'क्षेत्रफळ', value: tenant?.area || '— हे.' },
    { icon: '▰', label: 'रस्ते लांबी', value: tenant?.road_length || '— कि.मी.' },
    { icon: '💧', label: 'पर्जन्यमान', value: tenant?.rainfall || '— मि.मी.' },
    { icon: '♣', label: 'मुख्य व्यवसाय', value: tenant?.main_occupation || '—' },
  ];

  return (
    <div className="gp-home">
      <section className="gp-home-hero">
        <div className="gp-home-hero-media" aria-hidden="true">
          <img
            className="gp-home-hero-image"
            src={banner || '/HeaderBgImages/4.png'}
            alt=""
            onLoad={event => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (naturalWidth && naturalHeight) {
                event.currentTarget.parentElement?.style.setProperty('--hero-banner-ratio', String(naturalWidth / naturalHeight));
              }
            }}
          />
        </div>
        <div className="gp-home-hero-copy">
          <span className="hero-kicker">सुसंस्कार गाव · समृद्ध गाव · डिजिटल गाव</span>
          <h1>{shortName(tenant?.name)}</h1>
          <p className="hero-description">ग्रामविकासाच्या दिशेने एक पाऊल पुढे – पारदर्शक प्रशासन, जनतेसाठी समर्पित सेवा.</p>
          <div className="hero-stats">
            <div><span>👥</span><small>लोकसंख्या</small><strong>{formatMarathiNumber(tenant?.population || 3051)}</strong></div>
            <div><span>⌂</span><small>कुटुंबसंख्या</small><strong>{formatMarathiNumber(tenant?.total_households || 583)}</strong></div>
            <div><span>▤</span><small>साक्षरता दर</small><strong>{toMarathiDigits(tenant?.literacy_rate || '78.42%')}</strong></div>
            <div><span>♟</span><small>स्त्री पुरुष गुणोत्तर</small><strong>{toMarathiDigits(tenant?.sex_ratio || '930')}</strong></div>
          </div>
          <div className="hero-actions">
            <Link to={`/gp/${tenantSlug}/services`}>नागरी सेवा</Link>
            <Link to={`/gp/${tenantSlug}/notices`}>महत्त्वाच्या नोटीस</Link>
          </div>
        </div>
        {banners.length > 1 && (
          <div className="home-hero-dots" aria-label="बॅनर निवडा">
            {banners.map((item, index) => <button key={item.id || item.url} className={index === activeBanner ? 'active' : ''} onClick={() => setActiveBanner(index)} aria-label={item.title || `बॅनर ${index + 1}`} />)}
          </div>
        )}
      </section>

      {/* Notice Marquee Ticker Strip */}
      {notices.length > 0 && (
        <div className="home-notice-marquee-strip">
          <div className="marquee-badge">
            <span className="marquee-pulse-dot"></span>
            <span>📢 नोटीस अपडेट्स</span>
          </div>
          <div className="marquee-track-box">
            <marquee scrollamount="6" behavior="scroll" direction="left" onMouseOver={(e) => e.currentTarget.stop?.()} onMouseOut={(e) => e.currentTarget.start?.()}>
              {notices.map((notice, idx) => (
                <Link key={notice.id || idx} to={`/gp/${tenantSlug}/notices`} className="marquee-item">
                  <span className="marquee-tag">{formatCategory(notice.category)}</span>
                  <strong className="marquee-title">{notice.title}</strong>
                  {(notice.published_date || notice.publish_date || notice.createdAt) && (
                    <span className="marquee-date">({formatDate(notice.published_date || notice.publish_date || notice.createdAt)})</span>
                  )}
                  <span className="marquee-separator">✦</span>
                </Link>
              ))}
            </marquee>
          </div>
          <Link to={`/gp/${tenantSlug}/notices`} className="marquee-view-all">
            सर्व नोटीस →
          </Link>
        </div>
      )}

      <section className="home-services-section">
        <div className="section-title-row"><h2>नागरी सेवा</h2><Link to={`/gp/${tenantSlug}/services`}>सर्व सेवा पहा →</Link></div>
        <div className="service-grid">
          {services.map(item => (
            <Link key={item.type} to={`/gp/${tenantSlug}/services?service=${encodeURIComponent(item.type)}`} className="service-tile">
              <span className={`service-icon ${item.tone}`}>{item.icon}</span><strong>{item.label}</strong>
            </Link>
          ))}
          {!services.length && (
            <div className="notice-empty">
              सध्या कोणतीही ऑनलाइन प्रमाणपत्र सेवा उपलब्ध नाही.
            </div>
          )}
        </div>
      </section>

      <section className="metric-strip">
        {metrics.map(metric => <div key={metric.label}><span>{metric.icon}</span><p><small>{metric.label}</small><strong>{toMarathiDigits(metric.value)}</strong></p></div>)}
      </section>

      <section className="home-feature-grid">
        <div className="notice-panel">
          <div className="section-title-row"><h2>📜 नोटीस बोर्ड व सूचना</h2><Link to={`/gp/${tenantSlug}/notices`}>सर्व नोटीस पहा →</Link></div>
          <div className="notice-list">
            {notices.length > 0 ? (
              <div className="notice-list-scroll-box">
                <marquee
                  behavior="scroll"
                  direction="up"
                  scrollamount="3"
                  height="195"
                  onMouseOver={(e) => e.currentTarget.stop?.()}
                  onMouseOut={(e) => e.currentTarget.start?.()}
                >
                  {notices.map((notice, idx) => (
                    <Link key={notice.id || idx} to={`/gp/${tenantSlug}/notices`} className="home-notice-card">
                      <div className="notice-card-top-row">
                        <div className="notice-card-title-box">
                          <span className="notice-pin-icon">📌</span>
                          <strong className="notice-card-title" title={notice.title}>{truncateText(notice.title, 35)}</strong>
                          <span className="notice-category-badge">{formatCategory(notice.category)}</span>
                        </div>
                        <time className="notice-date-badge">📅 {formatDate(notice.published_date || notice.publish_date || notice.createdAt)}</time>
                      </div>
                      {notice.content && <p className="notice-card-snippet" title={notice.content}>{truncateText(notice.content, 20)}</p>}
                    </Link>
                  ))}
                </marquee>
              </div>
            ) : (
              <div className="notice-empty">सध्या कोणतीही नवीन नोटीस उपलब्ध नाही.</div>
            )}
          </div>
        </div>

        {villageIntro && (
          <article className="village-intro-section">
            <div className="village-intro-copy">
              <h2>गावाची ओळख</h2>
              <p>{villageIntro}</p>
              <Link to={`/gp/${tenantSlug}/about`}>अधिक वाचा →</Link>
            </div>
            {galleryImages[0]?.image_url && <Link to={`/gp/${tenantSlug}/about`} className="village-intro-image"><img src={galleryImages[0].image_url} alt={galleryImages[0].alt_text || galleryImages[0].title || 'गावाचे छायाचित्र'} /></Link>}
          </article>
        )}
      </section>

      <section className="home-discovery-section">
        <div className="section-title-row"><h2>ग्रामपंचायत पोर्टल पहा</h2></div>
        <div className="home-discovery-grid">
          {discoveryItems.map(item => (
            <Link key={item.label} to={`/gp/${tenantSlug}${item.path}`} className="home-discovery-card">
              <span className={`discovery-icon ${item.tone}`}>{item.icon}</span>
              <p><strong>{item.label}</strong><small>{item.detail}</small></p>
              <b>{formatMarathiNumber(item.count)}</b><i>→</i>
            </Link>
          ))}
        </div>
      </section>

      <section className="representative-section">
        <div className="section-title-row"><h2>जनप्रतिनिधी</h2></div>
        <div className="representative-row">
          {representatives.map((person, idx) => (
            <div className="representative-person" key={person.id || person.name || idx}>
              <img src={person.photo} alt={person.name} onError={event => { event.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(person.name)}&background=eaf2fc&color=06346c`; }} />
              <strong>{person.name}</strong><span>{person.designation}</span>
            </div>
          ))}
        </div>
      </section>

      {galleryImages.length > 0 && (
        <section className="home-gallery-section">
          <div className="section-title-row"><h2>फोटो गॅलरी</h2><Link to={`/gp/${tenantSlug}/gallery`}>संपूर्ण गॅलरी पहा →</Link></div>
          <div className="home-gallery-grid">
            {galleryImages.slice(0, 6).map(image => (
              <Link to={`/gp/${tenantSlug}/gallery`} key={image.id} className="home-gallery-card">
                <img src={image.image_url} alt={image.title || image.caption || 'गॅलरी'} />
                <span>{image.title || image.caption || 'गावाचे छायाचित्र'}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="home-contact-strip">
        <div><span>⌖</span><p><small>कार्यालय पत्ता</small><strong>{tenant?.address || `ग्रामपंचायत कार्यालय, ${shortName(tenant?.name)}`}</strong></p></div>
        <a href={phoneHref(tenant?.contact_phone || '+91 1234567890')}><span>☎</span><p><small>दूरध्वनी</small><strong>{tenant?.contact_phone || '+91 1234567890'}</strong></p></a>
        <a href={`mailto:${tenant?.contact_email || 'deulgaongadagram@gmail.com'}`}><span>✉</span><p><small>ईमेल</small><strong>{tenant?.contact_email || 'deulgaongadagram@gmail.com'}</strong></p></a>
        <div><span>◷</span><p><small>कार्यालय वेळ</small><strong>{tenant?.office_hours || '10:00 AM - 05:00 PM'}</strong></p></div>
      </section>
    </div>
  );
}
